import React from 'react';

interface Incident {
  Number: string;
  Priority: string;
  State: string;
  "Assigned to": string;
  "Short description": string;
  Opened: string;
  Duration: number;
  Impact: string;
  Service?: string;
  "Opened by": string;
}

interface IncidentOriginAnalysisProps {
  incidents: Incident[];
}

export function IncidentOriginAnalysis({ incidents }: IncidentOriginAnalysisProps) {
  // Group incidents by "Opened by"
  const incidentsByOrigin = incidents.reduce((acc, incident) => {
    const openedBy = incident["Opened by"] || "Unknown";
    if (!acc[openedBy]) {
      acc[openedBy] = [];
    }
    acc[openedBy].push(incident);
    return acc;
  }, {} as Record<string, Incident[]>);

  // Define AMS team members (you may need to adjust this list based on your team)
  const amsTeamMembers = [
    "Rishabh Yadav", "Shravan Singh", "Aman Singh", "Chhavi Jain", 
    "Shivam Singh", "Shama Farheen"
  ];

  // Define End Users
  const endUsers = [
    "Sebastian Petriw",
    "Arial Montalvo", 
    "David Berber",
 "   Veronica Jack",
    "Luis Cabello",
    "Yurii Kostenko",
    "Prasad Morye",
    "Jerwin Mascarinas",
    "Peter Ross",
    "Vaibhav Malhotra",
    "Daniel Cohen",
    "Mike Veldboer"
  ];

  // Separate by teams
  const amsTeamOrigins = Object.entries(incidentsByOrigin).filter(([openedBy]) => 
    amsTeamMembers.some(member => openedBy.includes(member))
  );

  const endUserOrigins = Object.entries(incidentsByOrigin).filter(([openedBy]) => 
    endUsers.includes(openedBy)
  );

  const integrationTeamOrigins = Object.entries(incidentsByOrigin).filter(([openedBy]) => 
    !amsTeamMembers.some(member => openedBy.includes(member)) && !endUsers.includes(openedBy)
  );

  const renderTableRow = (openedBy: string, incidents: Incident[]) => {
    const highPriorityCount = incidents.filter(i => i.Priority === "2 - High").length;
    const moderatePriorityCount = incidents.filter(i => i.Priority === "3 - Moderate").length;
    const normalPriorityCount = incidents.filter(i => i.Priority === "4 – Normal").length;

    return (
      <tr key={openedBy} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
          {openedBy}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {incidents.length}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {highPriorityCount}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {moderatePriorityCount}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {normalPriorityCount}
        </td>
      </tr>
    );
  };

  const renderTotalRow = (title: string, allIncidents: Incident[]) => {
    const totalIncidents = allIncidents.length;
    const totalHighPriority = allIncidents.filter(i => i.Priority === "2 - High").length;
    const totalModeratePriority = allIncidents.filter(i => i.Priority === "3 - Moderate").length;
    const totalNormalPriority = allIncidents.filter(i => i.Priority === "4 – Normal").length;

    return (
      <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">Total {title}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{totalIncidents}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{totalHighPriority}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{totalModeratePriority}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{totalNormalPriority}</td>
      </tr>
    );
  };

  const renderSectionHeader = (title: string) => (
    <tr className="bg-blue-50">
      <td colSpan={5} className="px-6 py-3 text-left text-sm font-bold text-blue-900 uppercase tracking-wider">
        📊 {title}
      </td>
    </tr>
  );

  const renderTableHeader = () => (
    <tr className="bg-gray-50">
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opened By</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Incidents</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">High Priority (P1 + P2)</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Moderate Priority (3)</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Normal Priority (4)</th>
    </tr>
  );

  // Calculate totals for each section
  const endUserIncidents = endUserOrigins.flatMap(([, incidents]) => incidents);
  const amsTeamIncidents = amsTeamOrigins.flatMap(([, incidents]) => incidents);
  const integrationTeamIncidents = integrationTeamOrigins.flatMap(([, incidents]) => incidents);

  return (
    <div className="mt-8">
      <h3 className="text-xl font-semibold mb-4">Incident Origin Analysis</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <tbody className="bg-white divide-y divide-gray-200">
            {/* End User Section */}
            {endUserOrigins.length > 0 && (
              <>
                {renderSectionHeader("End User")}
                {renderTableHeader()}
                {endUserOrigins.map(([openedBy, incidents]) => renderTableRow(openedBy, incidents))}
                {renderTotalRow("", endUserIncidents)}
              </>
            )}
            
            {/* Spacing between sections */}
            {(endUserOrigins.length > 0 && (amsTeamOrigins.length > 0 || integrationTeamOrigins.length > 0)) && (
              <tr className="bg-white">
                <td colSpan={5} className="py-2"></td>
              </tr>
            )}
            
            {/* AMS Team Section */}
            {amsTeamOrigins.length > 0 && (
              <>
                {renderSectionHeader("AMS Team")}
                {renderTableHeader()}
                {amsTeamOrigins.map(([openedBy, incidents]) => renderTableRow(openedBy, incidents))}
                {renderTotalRow("", amsTeamIncidents)}
              </>
            )}

            {/* Spacing between sections */}
            {(amsTeamOrigins.length > 0 && integrationTeamOrigins.length > 0) && (
              <tr className="bg-white">
                <td colSpan={5} className="py-2"></td>
              </tr>
            )}
            
            {/* Integration Team Section */}
            {integrationTeamOrigins.length > 0 && (
              <>
                {renderSectionHeader("Integration Team")}
                {renderTableHeader()}
                {integrationTeamOrigins.map(([openedBy, incidents]) => renderTableRow(openedBy, incidents))}
                {renderTotalRow("", integrationTeamIncidents)}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
} 